/**
 * The refusal a user gets when their address has asked for too many codes.
 *
 * `/auth/start` used to be limited only by IP, which is the wrong axis for a
 * cost measured in emails: one client could ask for 300 codes an hour inside
 * the rules, against a provider allowance of 500 that rotates the whole
 * application onto a fallback at 450. The server now keeps a rolling budget per
 * ADDRESS — see `otp-send-budget.ts` — and refuses with 429 and a body.
 *
 * Parsed structurally rather than with `instanceof ApiError`, for the same
 * reason `auth-error.ts` is: `api/client.ts` imports react-native and cannot be
 * loaded in a test at all, so a leaf module with no imports is the only shape
 * that can be driven under `node --test`.
 *
 * The countdown is deliberately NOT rendered here. The server's `retryAt` is an
 * instant, and the user reads the sentence some time after the response was
 * built; the screen recomputes from `retryAt` against the clock at the moment
 * it renders, which is the same rule `cue-cooldown.ts` follows.
 */

export type OtpLimitWindow = "short" | "day";

export type OtpSendLimitRefusal = {
  code: "OTP_SEND_LIMIT";
  window: OtpLimitWindow;
  /** ISO instant the ceiling lifts. */
  retryAt: string;
  /** Seconds, as computed when the response was built. A fallback only. */
  retryAfterSeconds: number;
};

type ErrorLike = { status?: unknown; body?: unknown };

/** The refusal, or null when this failure was something else entirely. */
export function otpSendLimitFrom(error: unknown): OtpSendLimitRefusal | null {
  const like = (error ?? {}) as ErrorLike;
  if (typeof like.status !== "number" || like.status !== 429) return null;

  const body = (like.body ?? {}) as Partial<OtpSendLimitRefusal>;
  if (body.code !== "OTP_SEND_LIMIT") return null;
  if (typeof body.retryAt !== "string") return null;

  return {
    code: "OTP_SEND_LIMIT",
    // Unknown windows read as the shorter one: naming a window we do not
    // recognise would be a guess, and the countdown carries the real answer.
    window: body.window === "day" ? "day" : "short",
    retryAt: body.retryAt,
    retryAfterSeconds:
      typeof body.retryAfterSeconds === "number" ? body.retryAfterSeconds : 0,
  };
}

/**
 * Seconds left from NOW, not from when the response was built.
 *
 * Floored at 1: a ceiling that lifts between the response and this call is
 * still a refusal, and "0 seconds" is not an answer. A `retryAt` that will not
 * parse falls back to the figure the server sent, so a malformed date degrades
 * to "slightly stale" rather than "one second".
 */
export function otpRetrySeconds(
  refusal: OtpSendLimitRefusal,
  now: number = Date.now(),
): number {
  const at = Date.parse(refusal.retryAt);
  if (Number.isNaN(at)) return Math.max(1, refusal.retryAfterSeconds);
  return Math.max(1, Math.ceil((at - now) / 1000));
}
